import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import {
  Home,
  Calendar,
  Mic,
  BookOpen,
  Settings,
  Menu,
  LogOut,
  Moon,
  Sun,
  Monitor,
  Crown,
  Sparkles,
  MessageSquarePlus,
  Send,
  Loader2,
  ChevronDown,
  ChevronUp,
  Shield,
} from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { changelogs, CURRENT_VERSION } from '@/lib/changelogs';
import { api } from '@/lib/api';
import { useSubscription } from '@/hooks/useSubscription';
import { usePageTracking } from '@/hooks/usePageTracking';

const navItems = [
  { path: '/', label: 'ホーム', icon: Home },
  { path: '/timeline', label: '過去のひとりごと', icon: Calendar },
  { path: '/record', label: 'はなす', icon: Mic },
  { path: '/dictionary', label: '辞書', icon: BookOpen },
  { path: '/settings', label: '設定', icon: Settings },
];

export default function AppLayout({ children }: { children: React.ReactNode }) {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const { isPremium } = useSubscription();
  usePageTracking();

  // GTM: ユーザー情報を dataLayer に送信（内部トラフィック除外に使用）
  useEffect(() => {
    if (user?.email) {
      window.dataLayer?.push({ user_email: user.email });
    }
  }, [user?.email]);

  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [themeMode, setThemeMode] = useState<'light' | 'dark' | 'system'>(
    () => (localStorage.getItem('theme') as 'light' | 'dark' | 'system') || 'light'
  );
  const [changelogOpen, setChangelogOpen] = useState(false);
  const [hasUnread, setHasUnread] = useState(
    () => localStorage.getItem('lastSeenVersion') !== CURRENT_VERSION
  );
  const [feedbackOpen, setFeedbackOpen] = useState(false);

  const cycleTheme = () => {
    const order: Array<'light' | 'dark' | 'system'> = ['light', 'dark', 'system'];
    const next = order[(order.indexOf(themeMode) + 1) % 3];
    setThemeMode(next);
    localStorage.setItem('theme', next);
    const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
    const shouldBeDark = next === 'dark' || (next === 'system' && prefersDark);
    document.documentElement.classList.toggle('dark', shouldBeDark);
    import('@/lib/themeManager').then(({ reapplyThemeForMode }) => reapplyThemeForMode());
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const toggleChangelog = () => {
    if (!changelogOpen) {
      localStorage.setItem('lastSeenVersion', CURRENT_VERSION);
      setHasUnread(false);
    }
    setChangelogOpen(!changelogOpen);
  };

  // Logo is handled via dark:hidden / hidden dark:block in JSX

  return (
    <div className="min-h-screen bg-background">
      {/* Mobile header */}
      <header className="sticky top-0 z-40 border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 lg:hidden">
        <div className="flex h-12 sm:h-14 items-center px-2 sm:px-4">
          <Button variant="ghost" size="icon" className="h-9 w-9 sm:h-10 sm:w-10" onClick={() => setSidebarOpen(true)}>
            <Menu className="h-5 w-5" />
          </Button>
          <Link to="/" className="ml-1 sm:ml-3">
            <img src="/logo.svg" alt="Hitorigoto" className="h-9 sm:h-11 dark:hidden" />
            <img src="/logo-dark.svg" alt="Hitorigoto" className="h-9 sm:h-11 hidden dark:block" />
          </Link>
          <div className="ml-auto flex items-center gap-1 sm:gap-2">
            <Button variant="ghost" size="icon" className="h-9 w-9 sm:h-10 sm:w-10" onClick={cycleTheme} title={themeMode === 'light' ? 'ライト' : themeMode === 'dark' ? 'ダーク' : 'システム'}>
              {themeMode === 'light' ? <Sun className="h-4 w-4" /> : themeMode === 'dark' ? <Moon className="h-4 w-4" /> : <Monitor className="h-4 w-4" />}
            </Button>
            <UserMenu user={user} onLogout={handleLogout} />
          </div>
        </div>
      </header>

      {/* Mobile sidebar (Sheet) */}
      <Sheet open={sidebarOpen} onOpenChange={setSidebarOpen}>
        <SheetContent side="left" className="w-64 p-0 flex flex-col">
          <SheetHeader className="p-4 border-b shrink-0">
            <SheetTitle>
              <img src="/logo.svg" alt="Hitorigoto" className="h-10 dark:hidden" />
              <img src="/logo-dark.svg" alt="Hitorigoto" className="h-10 hidden dark:block" />
            </SheetTitle>
          </SheetHeader>
          <nav className="flex-1 flex flex-col gap-1 p-2 overflow-y-auto">
            {navItems.map(item => (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => setSidebarOpen(false)}
                className={`flex items-center gap-3 rounded-lg px-3 py-2 text-sm transition-colors hover:bg-accent ${
                  location.pathname === item.path ? 'bg-accent text-accent-foreground font-medium' : 'text-muted-foreground'
                }`}
              >
                <item.icon className="h-4 w-4" />
                {item.label}
              </Link>
            ))}
            {!isPremium && (
              <Link
                to="/premium"
                onClick={() => setSidebarOpen(false)}
                className="flex items-center gap-3 rounded-lg px-3 py-2 text-sm transition-colors hover:bg-accent text-muted-foreground"
              >
                <Crown className="h-4 w-4" />
                プランをアップグレード
              </Link>
            )}
            {user?.role === 'admin' && (
              <Link
                to="/admin"
                onClick={() => setSidebarOpen(false)}
                className={`flex items-center gap-3 rounded-lg px-3 py-2 text-sm transition-colors hover:bg-accent ${
                  location.pathname.startsWith('/admin') ? 'bg-accent text-accent-foreground font-medium' : 'text-muted-foreground'
                }`}
              >
                <Shield className="h-4 w-4" />
                管理
              </Link>
            )}
          </nav>
          <div className="border-t px-2 py-2 shrink-0">
            <ChangelogSection open={changelogOpen} onToggle={toggleChangelog} hasUnread={hasUnread} onNavigate={() => setSidebarOpen(false)} />
            <button
              onClick={() => { setSidebarOpen(false); setFeedbackOpen(true); }}
              className="flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm text-muted-foreground transition-colors hover:bg-accent"
            >
              <MessageSquarePlus className="h-4 w-4" />
              改善要望を送る
            </button>
            <a
              href="https://discord.gg/qBxjXNjX"
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => setSidebarOpen(false)}
              className="flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm text-muted-foreground transition-colors hover:bg-accent"
            >
              <svg className="h-4 w-4" viewBox="0 0 24 24" fill="currentColor"><path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028c.462-.63.874-1.295 1.226-1.994a.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.095 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.095 2.157 2.42 0 1.333-.947 2.418-2.157 2.418z"/></svg>
              コミュニティ (Discord)
            </a>
          </div>
        </SheetContent>
      </Sheet>

      <div className="flex">
        {/* Desktop sidebar */}
        <aside className="hidden lg:flex lg:flex-col lg:w-64 lg:border-r lg:bg-background lg:fixed lg:inset-y-0">
          <div className="flex h-14 items-center px-6 border-b">
            <Link to="/">
              <img src="/logo.svg" alt="Hitorigoto" className="h-11 dark:hidden" />
              <img src="/logo-dark.svg" alt="Hitorigoto" className="h-11 hidden dark:block" />
            </Link>
          </div>
          <nav className="flex-1 flex flex-col gap-1 p-3">
            {navItems.map(item => (
              <Link
                key={item.path}
                to={item.path}
                className={`flex items-center gap-3 rounded-lg px-3 py-2 text-sm transition-colors hover:bg-accent ${
                  location.pathname === item.path ? 'bg-accent text-accent-foreground font-medium' : 'text-muted-foreground'
                }`}
              >
                <item.icon className="h-4 w-4" />
                {item.label}
              </Link>
            ))}
          </nav>
          {/* Premium upgrade (non-premium only) */}
          {!isPremium && (
            <div className="px-3 pb-1">
              <Link
                to="/premium"
                className="flex items-center gap-2 rounded-lg px-3 py-2 text-sm transition-colors hover:bg-accent text-muted-foreground"
              >
                <Crown className="h-4 w-4" />
                プランをアップグレード
              </Link>
            </div>
          )}
          {user?.role === 'admin' && (
            <div className="px-3 pb-1">
              <Link
                to="/admin"
                className={`flex items-center gap-2 rounded-lg px-3 py-2 text-sm transition-colors hover:bg-accent ${
                  location.pathname.startsWith('/admin') ? 'bg-accent text-accent-foreground font-medium' : 'text-muted-foreground'
                }`}
              >
                <Shield className="h-4 w-4" />
                管理
              </Link>
            </div>
          )}
          <div className="px-3 pb-1">
            <ChangelogSection open={changelogOpen} onToggle={toggleChangelog} hasUnread={hasUnread} />
            <button
              onClick={() => setFeedbackOpen(true)}
              className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-sm text-muted-foreground transition-colors hover:bg-accent"
            >
              <MessageSquarePlus className="h-4 w-4" />
              改善要望を送る
            </button>
            <a
              href="https://discord.gg/qBxjXNjX"
              target="_blank"
              rel="noopener noreferrer"
              className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-sm text-muted-foreground transition-colors hover:bg-accent"
            >
              <svg className="h-4 w-4" viewBox="0 0 24 24" fill="currentColor"><path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028c.462-.63.874-1.295 1.226-1.994a.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.095 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.095 2.157 2.42 0 1.333-.947 2.418-2.157 2.418z"/></svg>
              コミュニティ (Discord)
            </a>
          </div>
          <div className="border-t p-3 flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={cycleTheme} title={themeMode === 'light' ? 'ライト' : themeMode === 'dark' ? 'ダーク' : 'システム'}>
              {themeMode === 'light' ? <Sun className="h-4 w-4" /> : themeMode === 'dark' ? <Moon className="h-4 w-4" /> : <Monitor className="h-4 w-4" />}
            </Button>
            <UserMenu user={user} onLogout={handleLogout} side="top" align="start" />
          </div>
        </aside>

        {/* Main content */}
        <main className="flex-1 min-w-0 lg:ml-64">
          <div className="container mx-auto max-w-4xl px-3 py-3 sm:px-4 sm:py-4 lg:p-8">
            {children}
          </div>
        </main>
      </div>

      <FeedbackDialog open={feedbackOpen} onOpenChange={setFeedbackOpen} />
    </div>
  );
}

function ChangelogSection({ open, onToggle, hasUnread, onNavigate }: { open: boolean; onToggle: () => void; hasUnread: boolean; onNavigate?: () => void }) {
  const navigate = useNavigate();
  const displayLogs = changelogs.slice(0, 3);
  return (
    <div>
      <button
        onClick={onToggle}
        className="flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm text-muted-foreground transition-colors hover:bg-accent"
      >
        <Sparkles className="h-4 w-4" />
        <span className="flex-1 text-left">更新情報</span>
        {hasUnread && (
          <span className="h-2 w-2 rounded-full bg-blue-500" />
        )}
        {open ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
      </button>
      {open && (
        <div className="px-3 pb-2 space-y-1">
          {displayLogs.map(log => (
            <div key={log.version} className="text-xs text-muted-foreground pl-7">
              <span className="font-medium text-foreground/70">v{log.version}</span>
              <span className="mx-1">—</span>
              {log.summary}
            </div>
          ))}
          {changelogs.length > 3 && (
            <button
              onClick={() => { onNavigate?.(); navigate('/changelog'); }}
              className="text-xs text-primary hover:underline pl-7 mt-1"
            >
              すべて見る
            </button>
          )}
        </div>
      )}
    </div>
  );
}

function FeedbackDialog({ open, onOpenChange }: { open: boolean; onOpenChange: (open: boolean) => void }) {
  const [body, setBody] = useState('');
  const [rating, setRating] = useState<number | null>(null);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async () => {
    if (!body.trim() && rating == null) return;
    setSending(true);
    try {
      await api.submitFeedback(body.trim(), rating ?? undefined);
      setSent(true);
      setBody('');
      setRating(null);
      setTimeout(() => {
        onOpenChange(false);
        setSent(false);
      }, 1500);
    } catch {
      alert('送信に失敗しました。もう一度お試しください。');
    } finally {
      setSending(false);
    }
  };

  const getNpsColor = (n: number) => {
    if (n <= 6) return 'bg-red-500/15 text-red-700 dark:text-red-400 border-red-500/30';
    if (n <= 8) return 'bg-yellow-500/15 text-yellow-700 dark:text-yellow-400 border-yellow-500/30';
    return 'bg-green-500/15 text-green-700 dark:text-green-400 border-green-500/30';
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <MessageSquarePlus className="h-5 w-5" />
            改善要望
          </DialogTitle>
        </DialogHeader>
        {sent ? (
          <p className="text-center text-sm text-green-600 py-4">送信しました！ご意見ありがとうございます。</p>
        ) : (
          <div className="space-y-4">
            <div>
              <p className="text-sm font-medium mb-2">Hitorigotoを友人にすすめる可能性は？</p>
              <div className="flex gap-1">
                {Array.from({ length: 11 }, (_, i) => i).map(n => (
                  <button
                    key={n}
                    type="button"
                    onClick={() => setRating(n)}
                    className={`flex-1 h-9 rounded border text-xs font-medium transition-all ${
                      rating === n
                        ? getNpsColor(n) + ' ring-1 ring-current scale-105'
                        : 'bg-muted/50 text-muted-foreground border-transparent hover:bg-muted'
                    }`}
                  >
                    {n}
                  </button>
                ))}
              </div>
              <div className="flex justify-between mt-1">
                <span className="text-[10px] text-muted-foreground">全くない</span>
                <span className="text-[10px] text-muted-foreground">非常に高い</span>
              </div>
            </div>
            <Textarea
              placeholder="使いにくい点、欲しい機能、気づいたことなど自由にお書きください"
              value={body}
              onChange={e => setBody(e.target.value)}
              rows={4}
              className="resize-none"
            />
            <Button
              onClick={handleSubmit}
              disabled={(!body.trim() && rating == null) || sending}
              className="w-full"
            >
              {sending ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <Send className="h-4 w-4 mr-2" />
              )}
              送信する
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function UserMenu({ user, onLogout, side = 'bottom', align = 'end' }: { user: { name: string; email: string; picture?: string } | null; onLogout: () => void; side?: 'top' | 'bottom'; align?: 'start' | 'end' }) {
  const navigate = useNavigate();
  if (!user) return null;
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" className="rounded-full">
          <Avatar className="h-8 w-8">
            {user.picture && <AvatarImage src={user.picture} alt={user.name} />}
            <AvatarFallback>{user.name?.charAt(0)?.toUpperCase() || '?'}</AvatarFallback>
          </Avatar>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align={align} side={side} className="w-56">
        <div className="flex items-center gap-2 p-2">
          <Avatar className="h-8 w-8">
            {user.picture && <AvatarImage src={user.picture} alt={user.name} />}
            <AvatarFallback>{user.name?.charAt(0)?.toUpperCase() || '?'}</AvatarFallback>
          </Avatar>
          <div className="flex flex-col">
            <span className="text-sm font-medium">{user.name}</span>
            <span className="text-xs text-muted-foreground">{user.email}</span>
          </div>
        </div>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={() => navigate('/settings')}>
          <Settings className="mr-2 h-4 w-4" />
          設定
        </DropdownMenuItem>
        <DropdownMenuItem onClick={onLogout}>
          <LogOut className="mr-2 h-4 w-4" />
          ログアウト
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
